// needMenuActions — the SINGLE source of truth for a job posting's action menu.
// Both surfaces (the Rekrytering list ⋮ and the detail-page ⋮) build their menu
// from buildNeedMenuItems, so the two menus always have the exact same items in
// the exact same order, per state. Each surface supplies its own handlers and
// maps the neutral items to its local action type (RowAction / EntityAction) —
// see toRowActions / toEntityActions below.
import React from 'react';
import {
    Pencil, Eye, Copy, Link2, Power, PowerOff, RotateCcw, CalendarClock, XCircle, Trash2, Zap,
} from 'lucide-react';
import { LinkAvatar } from '@/components/icons/LinkAvatar';
import type { Need } from '@/lib/models/need';
import type { RowAction } from '@/components/custom-ui';
import type { EntityAction } from '@/components/custom-ui/EntityActionMenu';
import CONFIG from '@/lib/config';

export interface NeedActionHandlers {
    onEdit: () => void;
    onChatWithLink: () => void;
    onPreview: () => void;        // "go through as a candidate" — the /preview page
    onDuplicate: () => void;
    onCopyLink: () => void;       // public /jobb share link (active only)
    onPublish: () => void;        // draft → active
    onUnpublish: () => void;      // active → paused
    onReopen: () => void;         // paused|closed|filled → active
    onRelist: () => void;         // expired → active (new deadline)
    onClose: () => void;          // active|paused|expired → closed
    onDelete: () => void;
    onDevExpire?: () => void;     // dev-only test action
}

export interface NeedMenuItem {
    key: string;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    onClick: () => void;
    danger?: boolean;
    badge?: string;         // small uppercase pill (e.g. "dev")
    description?: string;   // secondary line under the label
}

// The canonical, ordered menu for a need in a given state. Edit both surfaces at
// once by editing THIS list.
export function buildNeedMenuItems(need: Need, h: NeedActionHandlers): NeedMenuItem[] {
    const items: NeedMenuItem[] = [
        { key: 'edit', label: 'Redigera', icon: Pencil, onClick: h.onEdit },
        { key: 'chat', label: 'Chatta med Link', icon: LinkAvatar, onClick: h.onChatWithLink },
        { key: 'preview', label: 'Förhandsgranska', icon: Eye, onClick: h.onPreview },
        { key: 'duplicate', label: 'Duplicera', icon: Copy, onClick: h.onDuplicate },
    ];

    // Public share link only works while the posting is live (/jobb 404s otherwise).
    if (need.status === 'active') {
        items.push({ key: 'copylink', label: 'Kopiera länk', icon: Link2, onClick: h.onCopyLink });
    }

    // Primary status transition for the current state (#64 confirm-gated downstream).
    if (need.status === 'draft') {
        items.push({ key: 'publish', label: 'Publicera', icon: Power, onClick: h.onPublish });
    } else if (need.status === 'expired') {
        items.push({ key: 'relist', label: 'Förläng & återpublicera', icon: CalendarClock, onClick: h.onRelist });
    } else if (need.status === 'closed' || need.status === 'filled') {
        items.push({ key: 'reopen', label: 'Återpublicera', icon: RotateCcw, onClick: h.onReopen });
    } else if (need.status === 'paused') {
        items.push({ key: 'reopen', label: 'Återpublicera', icon: Power, onClick: h.onReopen });
    } else if (need.status === 'active') {
        items.push({ key: 'unpublish', label: 'Avpublicera', icon: PowerOff, onClick: h.onUnpublish });
    }

    // Stäng — available while there's something live to close.
    if (need.status === 'active' || need.status === 'paused' || need.status === 'expired') {
        items.push({ key: 'close', label: 'Stäng annons', icon: XCircle, onClick: h.onClose });
    }

    items.push({ key: 'delete', label: 'Radera annons', icon: Trash2, danger: true, onClick: h.onDelete });

    if (CONFIG.SHOW_DEV_TOOLS && need.status === 'active' && h.onDevExpire) {
        items.push({
            key: 'dev-expire',
            label: 'Förfalla nu',
            icon: Zap,
            onClick: h.onDevExpire,
            badge: 'dev',
            description: 'Sätter sista ansökningsdag till igår så annonsen går ut direkt — för att testa utgången- och granskningsflödet.',
        });
    }

    return items;
}

// Map to the list-row action type (ContentEntityRow renders its own kebab).
export function toRowActions(items: NeedMenuItem[]): RowAction[] {
    return items.map((i) => ({
        icon: i.icon,
        label: i.label,
        onClick: i.onClick,
        badge: i.badge,
        description: i.description,
        ...(i.danger ? { tone: 'danger' as const } : {}),
    }));
}

// Map to the detail-page EntityActionMenu action type.
export function toEntityActions(items: NeedMenuItem[]): EntityAction[] {
    return items.map((i) => ({
        label: i.label,
        icon: i.icon,
        onClick: i.onClick,
        destructive: i.danger,
        badge: i.badge,
        description: i.description,
    }));
}
